import React, { useCallback, useState, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals } from "ramda";
import { notBlank } from "../../../util/ext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/pro-regular-svg-icons";

import IconButton from "@mui/material/IconButton";

import SearchIcon from "@mui/icons-material/Search";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";

const useStyles = hasFilterMenu =>
  makeStyles(theme => ({
    toolbar: {
      display: "flex",
      alignItems: "center",
      height: 60,
      padding: hasFilterMenu ? theme.spacing(0, 4) : 0,
    },

    search: {
      backgroundColor: theme.palette.primary.light,
      borderRadius: "4px",
      position: "relative",
      width: "100%",
      display: "flex",
      alignItems: "center",
    },

    searchIcon: {
      color: theme.palette.primary.darker,
    },

    inputRoot: {
      color: theme.palette.primary.darker,
      flexGrow: 1,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `2px solid ${theme.palette.primary.brandPrimary}`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `2px solid ${theme.palette.primary.brandPrimary}`,
      },
    },

    inputFocused: {
      borderColor: theme.palette.primary.brandPrimary,
    },

    outline: {
      borderColor: "transparent",
    },

    inputInput: {
      width: "100%",
      padding: theme.spacing(2.5),
    },
  }));

const SearchBar = ({ query, onQueryChange, filterMenu }) => {
  const classes = useStyles(notBlank(filterMenu))();

  const [searchTerm, setSearchTerm] = useState(query);
  useEffect(() => {
    if (query === "") {
      setSearchTerm("");
    }
  }, [query]);

  const handleSearchTermChange = useCallback(
    event => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm],
  );

  const handleSearchTermEnterKey = useCallback(
    event => {
      if (event.key === "Enter") {
        if (!equals(query, searchTerm)) {
          onQueryChange(searchTerm);
          setSearchTerm(searchTerm);
        }
      }
    },
    [searchTerm, query, onQueryChange, setSearchTerm],
  );

  const handleSearchTermClear = () => {
    onQueryChange("");
    setSearchTerm("");
  };

  return (
    <div className={classes.toolbar}>
      <div className={classes.search}>
        <OutlinedInput
          autoFocus
          type="search"
          placeholder="Search…"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
            notchedOutline: classes.outline,
            focused: classes.inputFocused,
          }}
          onChange={handleSearchTermChange}
          onKeyPress={handleSearchTermEnterKey}
          inputProps={{ "aria-label": "search" }}
          value={searchTerm}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                fontSize="large"
                classes={{ root: classes.searchIcon }}
              />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={handleSearchTermClear} size="large">
                <FontAwesomeIcon icon={faX} />
              </IconButton>
            </InputAdornment>
          }
        />
      </div>
      {notBlank(filterMenu) && filterMenu}
    </div>
  );
};

export default SearchBar;
